import * as React from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  ArrowLeft,
  Loader2,
  Pencil,
  PenLine,
  TriangleAlert,
} from 'lucide-react'
import { BOUNDARY_MIN_VERTICES } from '@lib/dto/geo.dto'
import { parseUuid } from '@lib/validators/uuid.validator'
import {
  BOUNDARY_PROBLEM_MESSAGES,
  type BoundaryProblem,
} from '@lib/validators/polygon.validator'
import { isPointInRing, metersToFeet } from '@lib/helpers/geo'
import { createSiteMap } from '@/map/basemap'
import { BoundaryLayer } from '@/map/BoundaryLayer'
import { BoundaryDraftBar } from '@/map/BoundaryDraftBar'
import { useBoundaryDraft, type BoundaryMode } from '@/map/useBoundaryDraft'
import { useMediaQuery, DESKTOP_QUERY } from '@/hooks/useMediaQuery'
import { MapZoomControls } from '@/map/MapZoomControls'
import { useSite } from '@/hooks/useSite'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useTrees } from '@/hooks/useTrees'
import { useUpdateSite } from '@/hooks/useSiteMutations'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAreaEditor, AreaChrome } from '@/pages/site-boundary/AreaEditor'
import { useSiteRecordRevisit } from '@/site-visits/useSiteRecordRevisit'
import {
  boundarySnapshot,
  editQuestion,
  type ChangeQuestion,
} from '@/site-visits/siteRecordChanges'

function formatAcres(acres: number): string {
  return `${acres.toFixed(2)} acres`
}

function formatPerimeter(meters: number): string {
  return `${Math.round(metersToFeet(meters)).toLocaleString('en-US')} ft`
}

function siteHint(mode: BoundaryMode): string | null {
  if (mode === 'view') return null
  if (mode === 'drawing') return 'Tap the map to add each corner'
  return 'Drag a point to move it · tap a faint point to add one'
}

function siteProblem(problem: BoundaryProblem | null): string | null {
  if (problem === null) return null
  return BOUNDARY_PROBLEM_MESSAGES[problem]
}

/** A site route whose id is not an id. The map must not mount for it. */
function MissingSite() {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3 bg-app px-6 text-center">
      <p className="text-sm font-medium">Site not found</p>
      <p className="text-sm text-muted-text">
        It may have been removed, or the link may be wrong.
      </p>
      <Button variant="outline" asChild>
        <Link to="/clients">Back to clients</Link>
      </Button>
    </div>
  )
}

/**
 * Draw and edit the outline of a site, and the areas inside it.
 *
 * Tap the map to lay down corners, drag them to adjust, tap the ghost handle
 * between two corners to add one in between. The outline is a draft until Save,
 * unlike the tree survey — see `useBoundaryDraft` for why the two differ.
 * An area is the same kind of draft: it is not part of the survey queue, and
 * nothing is stored until Save. The two are never edited at once.
 */
export function SiteBoundaryPage() {
  const params = useParams<{ siteId: string }>()
  const siteId = parseUuid(params.siteId)
  if (siteId === null) return <MissingSite />
  return <SiteBoundary siteId={siteId} />
}

/**
 * Where the back arrow goes: the survey that sent somebody here to draw, so
 * drawing the outline or the areas mid-survey does not strand them on the
 * client's page. Anything else goes back to the client.
 */
function backLink(
  from: string | null,
  siteId: string,
  clientId: string | null,
): { to: string; label: string } {
  if (from === 'trees') {
    return { to: `/sites/${siteId}/survey`, label: 'Back to the tree survey' }
  }
  if (from === 'irrigation') {
    return {
      to: `/sites/${siteId}/irrigation`,
      label: 'Back to the irrigation survey',
    }
  }
  return {
    to: clientId === null ? '/clients' : `/clients/${clientId}`,
    label: 'Back to client',
  }
}

function SiteBoundary({ siteId }: { siteId: string }) {
  const [searchParams] = useSearchParams()
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const mapRef = React.useRef<L.Map | null>(null)
  const layerRef = React.useRef<BoundaryLayer | null>(null)
  const didFitRef = React.useRef(false)
  const treeDotsRef = React.useRef<L.LayerGroup | null>(null)

  const site = useSite(siteId)
  const trees = useTrees(siteId)
  const updateSite = useUpdateSite()
  const draft = useBoundaryDraft(site.data?.boundary ?? null)
  // Once the site has been visited, a redrawn ring asks whether it changed.
  const isRevisit = useSiteRecordRevisit(siteId)
  const areas = useAreaEditor(siteId, draft.mode, mapRef, isRevisit)
  const areaLayerRef = areas.layerRef
  const areaDraftRef = areas.draftRef
  const paintAreas = areas.paint
  const detachAreas = areas.detach

  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [confirmingExclusion, setConfirmingExclusion] = React.useState(false)
  /** The answer a save was given, held while it asks about trees left out. */
  const [heldAnswer, setHeldAnswer] = React.useState<{
    isCorrection?: boolean | undefined
    changedOn?: string | undefined
  }>({})
  /** Pointer devices get zoom buttons and the app's usual control density. */
  const isDesktop = useMediaQuery(DESKTOP_QUERY)

  /*
   * Handlers reach the Leaflet layer through a ref so the layer can be built
   * once. Rebuilding it whenever a callback identity changed would tear down
   * and recreate every handle mid-drag.
   */
  const draftRef = React.useRef(draft)
  draftRef.current = draft

  /**
   * Trees that the outline AS CURRENTLY DRAWN would leave outside.
   *
   * Measured against the draft rather than the saved boundary, so pulling a
   * corner in shows immediately what it costs. Redrawing a boundary is the one
   * moment a survey can be silently orphaned — the trees do not move and
   * nothing warns you — which is exactly why it is worth saying here rather
   * than only on the survey screen afterwards.
   */
  const outsideCount = React.useMemo(() => {
    const rows = trees.data?.trees ?? []
    if (rows.length === 0 || draft.vertices.length < BOUNDARY_MIN_VERTICES) {
      return 0
    }
    return rows.filter((tree) => !isPointInRing(tree, draft.vertices)).length
  }, [trees.data, draft.vertices])

  /* -- map lifecycle -------------------------------------------------- */

  React.useEffect(() => {
    const container = containerRef.current
    if (container === null) return undefined

    const map = createSiteMap(container)

    // Scoped to this map instance — see the same note in FieldMapPage.
    didFitRef.current = false

    /*
     * Saved areas sit above the site outline and below the tree dots and the
     * vertex handles. The outline and the draft ring stay on Leaflet's overlay
     * pane; handles are markers, which are already above this pane.
     */
    const areaPane = map.createPane('site-areas')
    areaPane.style.zIndex = '450'
    const treePane = map.createPane('site-trees')
    treePane.style.zIndex = '480'

    const layer = new BoundaryLayer(map, {
      onMoveVertex: (index, point) => {
        draftRef.current.moveVertex(index, point)
      },
      onInsertVertex: (afterIndex, point) => {
        draftRef.current.insertVertex(afterIndex, point)
      },
      onSelectVertex: (index) => {
        draftRef.current.selectVertex(index)
      },
    })

    const areaLayer = new BoundaryLayer(map, {
      onMoveVertex: (index, point) => {
        areaDraftRef.current.moveVertex(index, point)
      },
      onInsertVertex: (afterIndex, point) => {
        areaDraftRef.current.insertVertex(afterIndex, point)
      },
      onSelectVertex: (index) => {
        areaDraftRef.current.selectVertex(index)
      },
    })

    map.on('click', (event: L.LeafletMouseEvent) => {
      const point = {
        latitude: event.latlng.lat,
        longitude: event.latlng.lng,
      }
      // An area being drawn owns the click. The site outline stays visible,
      // and its handles are hidden, so a tap cannot move both rings.
      const area = areaDraftRef.current
      if (area.mode === 'drawing') {
        area.appendVertex(point)
        return
      }
      if (area.mode === 'editing') {
        area.selectVertex(null)
        return
      }
      const current = draftRef.current
      if (current.mode === 'drawing') {
        current.appendVertex(point)
        return
      }
      // Tapping bare map while editing means "nothing selected", which is how
      // someone backs out of an accidental selection without an explicit
      // control for it.
      if (current.mode === 'editing') {
        current.selectVertex(null)
      }
    })

    mapRef.current = map
    layerRef.current = layer
    areaLayerRef.current = areaLayer
    // The editor's own effects ran before this map existed.
    paintAreas()

    return () => {
      layer.remove()
      areaLayer.remove()
      detachAreas()
      treeDotsRef.current?.remove()
      treeDotsRef.current = null
      map.remove()
      mapRef.current = null
      layerRef.current = null
    }
  }, [areaLayerRef, areaDraftRef, paintAreas, detachAreas])

  /* -- the trees, so you can trace around them ------------------------- */

  /*
   * Plain dots rather than the survey's full pins. Here the trees are a
   * reference for where the line should run, not the subject — species glyphs
   * and job badges would bury the outline being drawn. Amber marks one the
   * current draft excludes.
   */
  React.useEffect(() => {
    const map = mapRef.current
    if (map === null) return

    treeDotsRef.current?.remove()
    const rows = trees.data?.trees ?? []
    if (rows.length === 0) {
      treeDotsRef.current = null
      return
    }

    const hasRing = draft.vertices.length >= BOUNDARY_MIN_VERTICES
    const group = L.layerGroup(
      rows.map((tree) => {
        const outside = hasRing && !isPointInRing(tree, draft.vertices)
        return L.circleMarker(L.latLng(tree.latitude, tree.longitude), {
          radius: 4,
          weight: 1.5,
          pane: 'site-trees',
          className: outside
            ? 'fill-warning stroke-map-halo'
            : 'fill-map-halo stroke-map-ink/60',
          fillOpacity: 1,
          interactive: false,
        })
      }),
    )
    group.addTo(map)
    treeDotsRef.current = group
  }, [trees.data, draft.vertices])

  /* -- draw the site ring ---------------------------------------------- */

  React.useEffect(() => {
    const layer = layerRef.current
    if (layer === null) return
    layer.render({
      vertices: draft.vertices,
      // One ring is edited at a time. While an area is the draft, the site
      // outline stays on the map and its handles come off.
      editable: draft.mode !== 'view' && !areas.drawing,
      selectedIndex: draft.selectedIndex,
      invalid: draft.problem !== null,
      closed: draft.vertices.length >= 3,
      showMidpoints: draft.mode === 'editing',
    })
  }, [
    draft.vertices,
    draft.mode,
    draft.selectedIndex,
    draft.problem,
    areas.drawing,
  ])

  /* -- open on the outline --------------------------------------------- */

  React.useEffect(() => {
    const map = mapRef.current
    const layer = layerRef.current
    if (map === null || layer === null || didFitRef.current) return

    const saved = site.data?.boundary
    if (saved === undefined || saved === null) return

    const bounds = layer.bounds(saved.vertices)
    if (bounds === null) return
    map.fitBounds(bounds, { padding: [56, 56], maxZoom: 19 })
    didFitRef.current = true
  }, [site.data])

  /*
   * A site with no outline has nothing to center on — it has no coordinates of
   * its own. Its trees would be the obvious fallback, but a site being traced
   * for the first time usually has none either, so this leans on the device
   * instead: someone drawing a boundary is very often standing at the place.
   */
  React.useEffect(() => {
    if (didFitRef.current) return undefined
    /*
     * Wait until the site is actually loaded before falling back to the device.
     *
     * `site.data?.boundary != null` looks like it guards this, and does not:
     * while the request is in flight `site.data` is undefined, so the check
     * passes and the lookup starts anyway. A fast fix then framed the view and
     * claimed `didFitRef`, and the boundary — arriving a moment later — was
     * never framed at all. Being a race it failed intermittently on a phone,
     * and every time against a mocked instant fix.
     *
     * The precedence is deliberate: an existing outline is what this screen is
     * about, and where the device happens to be only matters when there is no
     * outline to show.
     */
    if (site.data === undefined) return undefined
    if (site.data.boundary !== null) return undefined
    if (!('geolocation' in navigator)) return undefined

    let canceled = false
    navigator.geolocation.getCurrentPosition(
      (position) => {
        /*
         * The lookup outlives the effect that started it — React re-runs
         * effects in development, and a person can navigate away mid-request.
         * `mapRef` is read here rather than captured above precisely so this
         * cannot write to a map that has since been destroyed, which throws
         * inside Leaflet rather than failing quietly.
         */
        const map = mapRef.current
        if (canceled || map === null || didFitRef.current) return
        map.setView(
          L.latLng(position.coords.latitude, position.coords.longitude),
          18,
        )
        didFitRef.current = true
      },
      // A refusal is not an error worth reporting here: the map stays where it
      // is and the person pans to the site themselves.
      () => undefined,
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    )

    return () => {
      canceled = true
    }
  }, [site.data])

  /* -- actions ---------------------------------------------------------- */

  /**
   * Save, or ask first if this outline leaves trees outside it.
   *
   * Redrawing a boundary changes which trees are counted as on the property,
   * and that is easy to do without noticing — the outline is what you are
   * looking at, the trees are not. So an outline that excludes anything asks
   * once, and names the number.
   *
   * It asks; it does not act. Nothing happens to the trees at all: they keep
   * their data, stay on the map, and are marked as outside. That is the whole
   * point of the warning being advisory — a tree really can sit off the
   * property, and a boundary traced roughly is a reason to look again rather
   * than to lose a morning's work.
   */
  function requestSave(isCorrection?: boolean, changedOn?: string): void {
    if (outsideCount > 0) {
      setHeldAnswer({ isCorrection, changedOn })
      setConfirmingExclusion(true)
      return
    }
    void handleSave(isCorrection, changedOn)
  }

  /**
   * `isCorrection` is the answer to "Changed, or drawn wrong?", if asked,
   * and `changedOn` the day it changed, if said.
   */
  async function handleSave(
    isCorrection?: boolean,
    changedOn?: string,
  ): Promise<void> {
    if (site.data === undefined) return
    setConfirmingExclusion(false)
    setSaveError(null)

    const boundary = draft.toSaveValue()
    try {
      const updated = await updateSite.mutateAsync({
        id: siteId,
        clientId: site.data.clientId,
        // Just the outline. The site's acreage IS this shape's area — the API
        // derives it on the way out — so there is nothing else to save and no
        // checkbox to forget to tick.
        input: {
          boundary,
          ...(isCorrection === undefined ? {} : { isCorrection }),
          ...(changedOn === undefined ? {} : { changedOn }),
        },
      })
      draft.reset(updated.boundary)
    } catch (caught) {
      setSaveError(
        isApiError(caught)
          ? (caught.body.message ?? 'Could not save the outline.')
          : toErrorMessage(caught),
      )
    }
  }

  /* -- render ------------------------------------------------------------ */

  /*
   * Note there is no early return for the loading state, and that is load
   * bearing. The map is created by an effect that reads a ref, so returning a
   * spinner instead of the container means the effect runs once, finds nothing,
   * and never runs again — leaving a permanently blank map once the site
   * arrives. Mounting the container immediately also lets tiles start
   * downloading while the record is still in flight.
   */
  if (site.isError) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-app px-6 text-center">
        <p className="text-sm font-medium">Site not found</p>
        <p className="text-sm text-muted-text">
          It may have been removed, or the link may be wrong.
        </p>
        <Button variant="outline" asChild>
          <Link to="/clients">Back to clients</Link>
        </Button>
      </div>
    )
  }

  const siteData = site.data ?? null
  const back = backLink(
    searchParams.get('from'),
    siteId,
    siteData?.clientId ?? null,
  )
  const active = areas.drawing ? areas.draft : draft
  const pointCount = active.vertices.length
  const hasShape = pointCount >= BOUNDARY_MIN_VERTICES
  const activeProblem = areas.drawing
    ? areas.problem
    : siteProblem(draft.problem)
  const hint = areas.drawing ? areas.hint : siteHint(draft.mode)

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-app">
      <header className="z-20 flex h-12 shrink-0 items-center gap-1 border-b border-default bg-surface px-2">
        <Button variant="ghost" size="icon" className="h-9 w-9" asChild>
          <Link to={back.to} aria-label={back.label}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">
            {siteData?.name ?? 'Loading…'}
          </p>
          <p className="truncate text-xs text-muted-text">Boundary and areas</p>
        </div>
      </header>

      <div className="relative min-h-0 flex-1">
        <div ref={containerRef} className="absolute inset-0 z-0" />

        {hint === null ? null : (
          <div className="pointer-events-none absolute top-2 left-1/2 z-10 max-w-[calc(100%-1rem)] -translate-x-1/2 rounded-full border border-default bg-surface/95 px-3 py-1 text-center text-xs shadow-menu">
            {hint}
          </div>
        )}

        {activeProblem !== null ? (
          <div className="absolute top-12 left-2 z-10 flex max-w-[calc(100%-1rem)] items-start gap-2 rounded-lg border border-danger bg-surface px-2.5 py-2 shadow-menu">
            <TriangleAlert className="mt-px h-4 w-4 shrink-0 text-danger" />
            <p className="text-xs leading-snug">{activeProblem}</p>
          </div>
        ) : null}

        {isDesktop ? (
          <MapZoomControls
            className="absolute right-3 bottom-3 z-10"
            onZoomIn={() => {
              mapRef.current?.zoomIn()
            }}
            onZoomOut={() => {
              mapRef.current?.zoomOut()
            }}
          />
        ) : null}

        {active.isFull ? (
          <div className="absolute bottom-2 left-2 z-10 rounded-lg border border-default bg-surface px-2.5 py-2 text-xs shadow-menu">
            That is as many points as an outline can hold.
          </div>
        ) : null}
      </div>

      <div className="z-20 shrink-0 border-t border-default bg-surface px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto max-w-5xl">
          {areas.drawing ? (
            <p className="text-xs text-muted-text">This area</p>
          ) : null}
          <BoundaryStats
            pointCount={pointCount}
            hasShape={hasShape}
            hasProblem={active.problem !== null}
            areaAcres={active.areaAcres}
            perimeterMeters={active.perimeterMeters}
            outsideCount={areas.drawing ? 0 : outsideCount}
          />

          {saveError !== null ? (
            <p className="mt-1.5 text-xs text-danger">{saveError}</p>
          ) : null}
          {areas.error !== null ? (
            <p className="mt-1.5 text-xs text-danger">{areas.error}</p>
          ) : null}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {renderControls()}
          </div>
        </div>
      </div>

      <Dialog open={confirmingExclusion} onOpenChange={setConfirmingExclusion}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              This outline leaves {outsideCount}{' '}
              {outsideCount === 1 ? 'tree' : 'trees'} outside it
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-secondary-text">
            <p>
              Nothing happens to {outsideCount === 1 ? 'it' : 'them'}. The{' '}
              {outsideCount === 1 ? 'tree keeps' : 'trees keep'} everything
              recorded, {outsideCount === 1 ? 'stays' : 'stay'} on the survey,
              and {outsideCount === 1 ? 'is' : 'are'} marked as off the site.
            </p>
            <p className="text-muted-text">
              Worth a look before saving, in case the outline is short rather
              than the {outsideCount === 1 ? 'tree' : 'trees'} being off the
              site.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmingExclusion(false)
              }}
            >
              Keep editing
            </Button>
            <Button
              onClick={() => {
                void handleSave(heldAnswer.isCorrection, heldAnswer.changedOn)
              }}
            >
              Save the outline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )

  /**
   * What saving a redrawn outline asks on a revisit. The first outline a site
   * has is the record being filled in, and asks nothing.
   */
  function boundaryQuestion(): ChangeQuestion | null {
    const saved = site.data?.boundary ?? null
    if (!isRevisit || saved === null) return null
    return editQuestion(
      'BOUNDARY',
      boundarySnapshot(saved),
      boundarySnapshot(draft.toSaveValue()),
    )
  }

  function renderControls(): React.ReactNode {
    if (siteData === null) {
      return (
        <span className="flex h-11 items-center gap-2 text-xs text-muted-text lg:h-9">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading site
        </span>
      )
    }

    if (areas.drawing) return <AreaChrome editor={areas} />

    if (draft.mode === 'view') {
      return (
        <div className="flex w-full flex-col gap-2">
          <AreaChrome editor={areas} />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              className="h-11 flex-1 lg:h-9"
              onClick={() => {
                if (hasShape) draft.startEditing()
                else draft.startDrawing()
              }}
            >
              {hasShape ? (
                <>
                  <Pencil className="h-4 w-4" />
                  Edit outline
                </>
              ) : (
                <>
                  <PenLine className="h-4 w-4" />
                  Draw boundary
                </>
              )}
            </Button>
            {hasShape ? (
              <Button
                type="button"
                variant="outline"
                className="h-11 lg:h-9"
                onClick={() => {
                  draft.startDrawing()
                }}
              >
                Redraw
              </Button>
            ) : null}
          </div>
        </div>
      )
    }

    return (
      <BoundaryDraftBar
        draft={draft}
        hasShape={hasShape}
        saveDisabled={!draft.canSave}
        pending={updateSite.isPending}
        onDiscard={() => {
          setSaveError(null)
          draft.cancel()
        }}
        onSave={requestSave}
        question={boundaryQuestion()}
        // Drawn at a desk, in no visit: a redrawn outline says when too.
        askWhen
      />
    )
  }
}

/**
 * The measurements, shown live while drawing.
 *
 * Area is the number that matters — it is what work gets quoted against, and
 * once this outline is saved it simply IS the site's acreage. Showing it as the
 * shape changes means a mistraced corner is obvious immediately, rather than
 * being discovered when a quote comes out wrong.
 */
function BoundaryStats({
  pointCount,
  hasShape,
  hasProblem,
  areaAcres,
  perimeterMeters,
  outsideCount,
}: {
  pointCount: number
  hasShape: boolean
  hasProblem: boolean
  areaAcres: number
  perimeterMeters: number
  outsideCount: number
}) {
  /*
   * A crossed outline still yields a number — the shoelace sum happily returns
   * the difference between its two lobes — and that number is meaningless.
   * Printing it beside a banner saying the shape has no area to measure would
   * contradict the banner, and someone skimming would take the figure.
   */
  if (hasShape && hasProblem) {
    return (
      <p className="text-xs text-danger">
        No area to measure while the outline is crossed.
      </p>
    )
  }

  if (!hasShape) {
    return (
      <p className="text-xs text-muted-text">
        {pointCount === 0
          ? 'No outline yet.'
          : `${String(pointCount)} of ${String(BOUNDARY_MIN_VERTICES)} points needed to enclose an area.`}
      </p>
    )
  }

  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
      <span className="text-sm font-semibold tabular-nums">
        {formatAcres(areaAcres)}
      </span>
      <span className={cn('text-xs text-muted-text tabular-nums')}>
        {formatPerimeter(perimeterMeters)} around
      </span>
      <span className="text-xs text-muted-text tabular-nums">
        {pointCount} points
      </span>
      {outsideCount > 0 ? (
        <span className="flex items-center gap-1 text-xs text-warning">
          <TriangleAlert className="h-3.5 w-3.5" />
          {outsideCount} {outsideCount === 1 ? 'tree' : 'trees'} outside
        </span>
      ) : null}
    </div>
  )
}
